import React, { useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { extent } from "d3-array";

import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";

import Pipeline from "../Pipeline";
import Filter from "../../Filter";

import TuerenReducer from "./TuerenReducer";
import TuerenIndicator from "./TuerenIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities: { vehicle },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const [maximum, setMaximum]: [number | undefined, any] = useState();
  const [minimum, setMinimum]: [number | undefined, any] = useState();
  const [range, setRange]: [[number, number] | undefined, any] = useState();

  React.useEffect(() => {
    if (!vehicle) return;
    const range: [any, any] = extent(
      Object.values(vehicle)
        .map((e: any) => e.tueren)
        .filter((e: any) => !!e),
    );
    setRange(range);
  }, [vehicle, dispatchFilter]);

  React.useEffect(
    () => {
      if (!(vehicle && range)) return;
      if (!value) {
        setMinimum(range[0]);
        setMaximum(range[1]);
      } else {
        setMinimum(value[0]);
        setMaximum(value[1]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, range],
  );

  const handleChange = (element: any, newValue: any) => {
    setMinimum(newValue[0]);
    setMaximum(newValue[1]);
  };

  const submit = (element: any, value: any) => {
    const newValue =
      JSON.stringify(range) === JSON.stringify(value) ? null : value;
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { tueren: newValue },
    });
  };

  if (!(minimum && maximum && range)) return null;

  const marks = [
    { value: range[0], label: range[0] },
    { value: range[1], label: range[1] },
  ];

  if (minimum > range[0]) marks.push({ value: minimum, label: minimum });
  if (maximum < range[1] && minimum !== maximum)
    marks.push({ value: maximum, label: maximum });

  const options = {
    min: range[0],
    max: range[1],
    onChange: handleChange,
    onChangeCommitted: submit,
    marks,
  };

  const Component = (
    <Box sx={{ px: 1 }}>
      <Slider
        getAriaLabel={(i) => "Türen " + i}
        value={[minimum, maximum]}
        {...options}
      />
    </Box>
  );

  return (
    <Filter
      component={Component}
      open={false}
      title={
        <FormattedMessage id="filter.tueren.label" defaultMessage="Türen" />
      }
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities: { vehicle },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const [range, setRange]: [[number, number] | undefined, any] = useState();

  React.useEffect(() => {
    if (!vehicle) return;
    const range: [any, any] = extent(
      Object.values(vehicle)
        .map((e: any) => e.tueren)
        .filter((e: any) => !!e),
    );
    setRange(range);
  }, [vehicle, dispatchFilter]);

  if (!range) return null;

  const setValueHandler = (event: SelectChangeEvent) => {
    // Check if all variants are selected
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? null : [value, range[1]];
    dispatchFilter({ type: "UPDATE_FILTER", payload: { tueren: newValue } });
  };

  var options = [];
  for (var i = range[0]; i <= range[1]; i++) {
    options.push({
      value: i,
      title: `${i} ${i === 1 ? "Tür" : "Türen"}`,
    });
  }

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-tueren-simple-label">
        <FormattedMessage
          id="filter.tueren-from.label"
          defaultMessage="Anzahl Türen ab"
        />
      </InputLabel>
      <Select
        labelId="filter-tueren-simple-label"
        id="filter-tueren-simple"
        value={!value ? "0" : value[0].toString()}
        label={t({
          id: "filter.tueren-from.label",
          defaultMessage: "Anzahl Türen ab",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"tueren-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}
const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        type: "tueren",
        defaultValue: null,
        Component: Filter,
        Reducer: TuerenReducer,
        Indicator: TuerenIndicator,
      }}
    />
  );
};

export default Container;
